import { Flex, Typography, Loader, MarkdownPreview, ButtonText } from '@percent/lemonade'
import { CurrencyCode, formatMoney, useQuery } from '@percent/utility'
import { useTranslation } from 'react-i18next'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { useLocale } from '@percent/cause-dashboard/common/hooks/useLocale/useLocale'
import {
  CandidateFormQuestion,
  FormSubmissionAnswer
} from '@percent/cause-dashboard/services/grants/grantsService.types'
import { TaskSubmissionViewProps } from './TaskSubmissionView.types'
import { ErrorState } from '../../../components/errorState/ErrorState'
import { buildMoneyGroups, moneyRenderDecision } from '../candidateTaskForm/money/groupMoneyQuestions'
import styles from './TaskSubmissionView.module.scss'

export function TaskSubmissionView({
  programId,
  candidateId,
  taskId,
  formId,
  onDocumentsClick
}: Readonly<TaskSubmissionViewProps>) {
  const { t } = useTranslation()
  const { grantsService } = useServices()
  const { locale } = useLocale()

  const { data: submissionsData, isLoading: isSubmissionsLoading } = useQuery(
    ['/form-submissions', programId, candidateId],
    grantsService.getCandidateFormSubmissions,
    {
      programId,
      candidateId
    }
  )

  const { data: formData, isLoading: isFormLoading } = useQuery(
    ['/forms', formId, programId, locale],
    grantsService.getCandidateForm,
    {
      programId,
      candidateId,
      formId,
      locale
    }
  )

  if (isSubmissionsLoading || isFormLoading) {
    return <Loader />
  }

  const submissions = submissionsData ?? []
  const submission = submissions.find(sub => sub.taskId === taskId)
  const candidateForm = formData?.candidateForm

  if (!submission || !candidateForm) {
    return <ErrorState />
  }

  const answersByQuestionId = new Map<string, FormSubmissionAnswer>(
    submission.formAnswers.map(answer => [answer.questionId, answer])
  )

  const findAnswer = (questionId: string) => answersByQuestionId.get(questionId)

  const formatMoneyAnswer = (amountAnswer: string, currency?: CandidateFormQuestion) => {
    const currencyCode = ((currency && findAnswer(currency.id)?.answer) ||
      (currency?.type === 'money' && currency.defaultCurrency) ||
      'USD') as CurrencyCode
    const minorAmount = Number(amountAnswer)

    return Number.isNaN(minorAmount)
      ? amountAnswer
      : formatMoney({ amount: minorAmount, currency: currencyCode }, { locale })
  }

  return (
    <Flex direction="column" gap={16}>
      <Typography variant="h4">{candidateForm.displayName}</Typography>

      {candidateForm.pages
        ?.sort((a, b) => a.order - b.order)
        .map((page, index) => {
          const moneyGroups = buildMoneyGroups(page.questions)

          return (
            <Flex key={page.id} className={styles.pageWrapper} data-is-last={index === candidateForm.pages.length - 1}>
              {page.title !== candidateForm.title && (
                <Typography variant="h5" className={styles.pageTitle}>
                  {page.title}
                </Typography>
              )}

              <Flex direction="column" gap={16}>
                {page.questions
                  ?.sort((a, b) => a.order - b.order)
                  .map(question => {
                    const money = moneyRenderDecision(question, moneyGroups)

                    if (money) {
                      const moneyAnswer = findAnswer(question.id)?.answer

                      if (!money.render || !moneyAnswer) return null

                      return (
                        <Flex key={question.id} direction="column" gap={4} className={styles.questionWrapper}>
                          <Typography variant="default-bold" className={styles.questionLabel}>
                            {(money.group.amount ?? question).label}
                          </Typography>
                          <Typography variant="default" className={styles.answerText}>
                            {money.currencyOnly ? moneyAnswer : formatMoneyAnswer(moneyAnswer, money.group.currency)}
                          </Typography>
                        </Flex>
                      )
                    }

                    const answer = findAnswer(question.id)
                    // Prefer the form definition's label (localised via the locale-keyed getCandidateForm fetch)
                    // over the submission's questionLabel, which is a snapshot stored in the original language.
                    const questionLabel = question.label ?? answer?.questionLabel
                    const answerType = answer?.type ?? question.type
                    // Prefer the AI-translated answer for the active locale, falling back to the
                    // raw answer, which is stored in the language the candidate originally submitted.
                    const answerText = answer?.translatedAnswer?.localizedAnswers?.[locale] ?? answer?.answer ?? ''

                    if (!answer && question.type !== 'documents') {
                      return null
                    }

                    return (
                      <Flex key={question.id} direction="column" gap={4} className={styles.questionWrapper}>
                        {answerType !== 'documents' && (
                          <Typography variant="default-bold" className={styles.questionLabel}>
                            {questionLabel}
                          </Typography>
                        )}

                        {answerType === 'documents' ? (
                          <Typography variant="default" className={`${styles.answerText} ${styles.documentsText}`}>
                            {t('grants.grantDetails.candidateTasks.documentsPrefix')}
                            <ButtonText onClick={onDocumentsClick} data-testid="view-documents-link" variant="primary">
                              {t('grants.grantDetails.candidateTasks.documentsLink')}
                            </ButtonText>
                            {t('grants.grantDetails.candidateTasks.documentsSuffix')}
                          </Typography>
                        ) : answerType === 'textarea' ? (
                          <MarkdownPreview content={answerText} className={styles.answerText} fontSize="bodySmall" />
                        ) : (
                          <Typography variant="default" className={styles.answerText}>
                            {answerText}
                          </Typography>
                        )}
                      </Flex>
                    )
                  })}
              </Flex>
            </Flex>
          )
        })}
    </Flex>
  )
}
